import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { Typography, Divider, Icon, Tooltip, Button } from 'shared/ui'
import { numberFormatIntUsd, numberFormat } from 'shared/lib'
import { TOOLTIP_TEXT } from 'pages/settings/pages/billing/pages/overview/ui/OpportunityModal/constants/constants'
import { type RecommendedPlanData } from 'pages/settings/pages/billing/pages/overview/ui/OpportunityModal/store/types'
import styles from './styles.module.scss'

type IRecommendedPlanCardProps = {
  data: RecommendedPlanData
  planBasePrice: number
  showAdditionalPriceInfo: boolean
  showGreyBorder: boolean
  suggestedMonthlyTotalPriceYearly: number
  suggestedYearlyPlanTotalPrice: number
  onUpgrade: () => void
  onSeeOtherPlans: () => void
  hasTimePeriodTabs: boolean
}

export const RecommendedPlanCard = observer(
  ({
    data,
    planBasePrice,
    showAdditionalPriceInfo,
    showGreyBorder,
    suggestedYearlyPlanTotalPrice,
    onUpgrade,
    onSeeOtherPlans,
    hasTimePeriodTabs,
  }: IRecommendedPlanCardProps) => {
    const displayPrice = showAdditionalPriceInfo
      ? suggestedYearlyPlanTotalPrice / 12
      : data.totalPrice
    const displayPeriod = showAdditionalPriceInfo ? 'month, billed annually' : data.period
    const priceColor = showAdditionalPriceInfo
      ? 'var(--green-80)'
      : 'var(--content-primary-primary)'

    return (
      <div
        className={classNames(styles.planInfo, styles.recommendedPlanInfo, {
          [styles.greenBorder]: !showGreyBorder,
        })}
      >
        <Icon icon='arrow_09' fontSize={72} className={styles.arrowIcon} />

        <Typography ariaLabel='opportunity_modal_recommended_plan_title' variant='body-lg-semibold'>
          Recommended plan
        </Typography>
        <div className={styles.getOffCost}>
          <Typography
            ariaLabel='opportunity_modal_save_cost'
            variant='body-md-medium'
            color='var(--green-100)'
          >
            Get {numberFormatIntUsd({ value: data.saveCost })} off
          </Typography>
        </div>
        <div>
          <Typography
            ariaLabel='opportunity_modal_payment_cost_amount'
            variant='body-xxl-semibold'
            color={priceColor}
          >
            {numberFormatIntUsd({ value: displayPrice })}
          </Typography>
          <Typography
            ariaLabel='opportunity_modal_payment_period'
            variant='body-rg-regular'
            className={styles.period}
          >
            / {displayPeriod}
          </Typography>
        </div>

        <Typography
          ariaLabel='opportunity_modal_total_credits'
          variant='body-lg-semibold'
          color='var(--content-primary-primary)'
          className={classNames(styles.totalCredits, {
            [styles.withoutTimePeriod]: !hasTimePeriodTabs,
          })}
        >
          {numberFormat({ value: data.credits + data.extraCredits })} total credits
        </Typography>

        <div className={classNames(styles.dividerWrapper, styles.dividerDefault)}>
          <Divider className={styles.divider} />
        </div>
        <div className={styles.creditsRow}>
          <Typography ariaLabel='opportunity_modal_plan_credits_info' variant='body-lg-regular'>
            {numberFormat({ value: data.credits })} plan credits / {data.period}
          </Typography>
          <Typography ariaLabel='opportunity_modal_plan_credits_price' variant='body-lg-semibold'>
            {numberFormatIntUsd({ value: planBasePrice })}
          </Typography>
        </div>
        <div className={classNames(styles.creditsRow, styles.creditsRowWithMargin)}>
          <Typography ariaLabel='opportunity_modal_overage_credits_info' variant='body-lg-regular'>
            {numberFormat({ value: data.extraCredits })} overage credits
            <Tooltip label={TOOLTIP_TEXT} placement='top'>
              <Icon
                icon='question'
                fontSize={14}
                color='var(--content-neutral-primary)'
                className={styles.overageCreditsIcon}
              />
            </Tooltip>
          </Typography>
          <Typography ariaLabel='opportunity_modal_overage_credits_cost' variant='body-lg-semibold'>
            {numberFormatIntUsd({ value: data.extraPrice })}
          </Typography>
        </div>
        <div className={styles.additionalCreditPriceDifferenceContainer}>
          <Typography
            ariaLabel='opportunity_modal_additional_credit_price'
            variant='body-md-regular'
            color='var(--content-primary-tertiary)'
          >
            {numberFormat({
              value: data.additionalCreditPrice,
              currency: 'USD',
              minimumFractionDigits: 2,
              maximumFractionDigits: 4,
            })}
            &nbsp; per additional credit
          </Typography>
          {data.additionalCreditPriceDifference > 0 && (
            <Typography
              ariaLabel='opportunity_modal_additional_credit_price_difference'
              variant='body-xs-medium'
              color='var(--green-90)'
              className={styles.additionalCreditPriceDifference}
            >
              {numberFormat({
                value: data.additionalCreditPriceDifference,
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })}
              % off
            </Typography>
          )}
        </div>

        <Button
          text='Review and confirm'
          typeBtn='contained'
          contained='primary'
          size='large'
          className={styles.upgradePlanButton}
          fullWidth
          onClick={onUpgrade}
        />

        <div className={styles.needDifferentPlan}>
          <Typography
            ariaLabel='opportunity_modal_need_different_plan'
            variant='body-sm-regular'
            color='var(--content-primary-secondary)'
            className={styles.needDifferentPlanText}
          >
            Need a different plan?
            <Button
              text='See other plans'
              typeBtn='link'
              size='small'
              className={styles.seeOtherPlansButton}
              onClick={onSeeOtherPlans}
            />
          </Typography>
        </div>
      </div>
    )
  }
)
